(ns ductile.clerk.render
  (:require ["@radix-ui/react-hover-card" :as hover-card]
            ["@turf/turf" :as turf]
            ["d3-scale" :as d3-scale]
            ["html5sortable/dist/html5sortable.cjs" :as sortable]
            [clojure.pprint]
            [clojure.set :as set]
            [clojure.string :as str]
            [ductile.clerk.viewer :as viewer]
            [ductile.time.format :as time-format]
            [ductile.time.js-types :as time]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mapbox :as mapbox]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.render.localstorage :as localstorage]))

(defn ->hidden-paths [schema ks]
  (set (viewer/head->paths (viewer/expand-keys-to-head schema ks))))

(defn context-menu-item [!table-state {:as item :keys [key path paths hidden? items]}]
  (if (seq items)
    [:div
     [context-menu-item !table-state (-> item (dissoc :items) (assoc :paths (map :path items)))]
     (into [:div.ml-3]
           (map (fn [item]
                  [context-menu-item !table-state item]))
           items)]
    [:div.pl-3.pr-2.py-1.flex.items-center.gap-2.border-b.border-slate-100.hover:bg-indigo-50
     [:div.flex-auto.pr-4 {:class (when hidden? "opacity-50")}
      (get-in @!table-state [:translated-keys key] (tr key))]
     [:div.flex.items-center.justify-center.hover:text-indigo-600.cursor-pointer
      {:style {:width 12 :height 12}
       :class (when hidden? "opacity-50")
       :on-click (fn []
                   (let [{:keys [hide-columns schema]} @!table-state
                         new-hidden-paths (let [paths (set (if (seq paths) paths [path]))
                                                hidden-paths (->hidden-paths schema hide-columns)]
                                            (if hidden?
                                              (set/difference hidden-paths paths)
                                              (set/union hidden-paths paths)))]
                     (swap! !table-state assoc
                            :hide-columns (viewer/paths->head new-hidden-paths))))}
      (if hidden? icons/eye-slash icons/eye)]]))

(defn menu-state-item [{:keys [hidden-paths]} path]
  {:key (last path)
   :path path
   :hidden? (contains? hidden-paths path)})

(defn ->menu-state [{:as table :keys [column-order hide-columns schema]}]
  (let [hidden-paths (->hidden-paths schema hide-columns)
        paths (viewer/head->paths schema)
        table (-> table
                  (assoc :hidden-paths hidden-paths)
                  (assoc :schema (-> (viewer/->ordered-paths column-order paths) viewer/paths->head)))]
    (map (fn [k]
           (if (vector? k)
             {:key (first k)
              :path [(first k)]
              :hidden? (set/subset? (set (map #(vector (first k) %) (last k))) hidden-paths)
              :items (map #(menu-state-item table [(first k) %]) (last k))}
             (menu-state-item table [k])))
         (:schema table))))

(defn vec-remove [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn vec-add [coll pos el]
  (concat (subvec coll 0 pos) [el] (subvec coll pos)))

(defn vec-move [coll pos1 pos2]
  (let [el (nth coll pos1)]
    (if (= pos1 pos2)
      coll
      (into [] (vec-add (vec-remove coll pos1) pos2 el)))))

(defn on-sort-update [!table-state event]
  (let [orig (.. event -detail -origin -index)
        dest (.. event -detail -destination -index)
        {:keys [hidden-paths schema]} @!table-state
        new-order (vec-move (vec schema) orig dest)]
    (js/requestAnimationFrame ;; Wait until DOM is done…
     (fn []
       (swap! !table-state assoc
              :schema new-order
              :column-order new-order)))))

(defn context-menu
  ([!table-state] [context-menu {} !table-state])
  ([opts !table-state]
   (let [table @!table-state
         !el (hooks/use-ref nil)]
     (hooks/use-effect
      (fn []
        (let [sort-el (first (sortable @!el #js {:forcePlaceholderSize true}))]
          (.addEventListener sort-el "sortupdate" (partial on-sort-update !table-state))
          (when-let [k (:local-storage-key opts)]
            (localstorage/set-item! k (select-keys @!table-state [:hide-columns :column-order])))))
      [@!table-state])
     [:div.rounded-lg.border.border-slate-300.shadow-xl.bg-white.text-sm.overflow-y-auto.user-select-none
      {:style {:width 250 :max-height 500}}
      [:div {:ref !el}
       (map (fn [item]
              ^{:key item}
              [context-menu-item !table-state item])
            (->menu-state table))]])))

(def direction-vis-scale
  (.. (d3-scale/scaleLinear)
      (domain #js [0 500])
      (range #js [0 1])
      (clamp true)))

(defn geocoded? [location]
  (and (number? (:location/longitude location))
       (number? (:location/latitude location))))

(defn get-bearing [from to]
  (when (and (geocoded? from) (geocoded? to))
    (turf/bearing #js[(:location/longitude from) (:location/latitude from)]
                  #js[(:location/longitude to) (:location/latitude to)])))

(defn get-distance [from to]
  (when (and (geocoded? from) (geocoded? to))
    (turf/distance #js[(:location/longitude from) (:location/latitude from)]
                   #js[(:location/longitude to) (:location/latitude to)]
                   #js{:units "kilometers"})))

(defn cardinal-direction
  "Circle plus line which shows the general direction and distance a car needs to
  travel. 500km is the distance to the edge of the circle."
  ([m]
   [cardinal-direction {:radius 12 :max-height 28} m])
  ([{:keys [radius max-height]
     :or {radius 12
          max-height 28}}
    {:cardinal-direction/keys [from to]}]
   (when (and (geocoded? from)
              (geocoded? to))
     (let [bearing (get-bearing from to)
           distance (get-distance from to)
           stroke 2]
       [:div.flex.justify-center.items-center
        {:style {:transform-origin "center"
                 :max-height (str max-height "px")
                 :line-height 0}}
        [:svg {:style {:width (* radius 2)
                       :height (* radius 2)
                       :max-height (str max-height "px")
                       :display "block"}}
         [:g
          [:circle {:cx radius :cy radius :r (- radius (/ stroke 2)) :fill "#eee" :stroke "#fff" :strokeWidth stroke :strokeLinecap "round"}]
          [:line {:stroke "#ffffff" :strokeWidth 1 :strokeLinecap "round"
                  :x1 radius :y1 0 :x2 radius :y2 (* 2 radius)}]
          [:line {:stroke "#ffffff" :strokeWidth 1 :strokeLinecap "round"
                  :x1 0 :y1 radius :x2 (* 2 radius) :y2 radius}]]
         [:g {:style {:transform (str "rotate(" (js/Math.round bearing) "deg)")
                      :transform-origin "center"}}
          [:line {:stroke "#0091FF" :strokeWidth 3 :strokeLinecap "round"
                  :x1 radius :y1 radius :x2 radius :y2 (- radius (* radius (direction-vis-scale distance)))}]]]]))))

(defn priority
  "Widget which visualizes how many days are left, or have passed, since the car
  is expected to arrive."
  ([t]
   [priority {:size 1 :max-height 28} t])
  ([{:keys [size max-height]
     :or {size 1
          max-height 28}}
    {date-time :priority/date}]
   (let [priority-color-scale (.. (d3-scale/scaleLinear)
                                  (domain #js[-5 0 5])
                                  (range #js["#3e9b4f" "#cdced6" "#ce2c31"])
                                  (clamp true))]
     (when-let [days-left (when date-time
                            (dec (time/days-between (time/today) date-time)))]
       [:> hover-card/Root {:open-delay 150 :close-delay 0}
        [:> hover-card/Trigger
         (into
          [:div.flex.items-center.justify-center
           {:style {:max-height (str max-height "px")}}]
          (for [day (range -5 6)]
            [:div.rounded-full
             {:style (cond-> {:width (case size
                                       1 1.5
                                       2 4)
                              :height (+ (case size
                                           1 6
                                           2 13)
                                         (* (js/Math.abs day) 2))
                              :opacity 0.25
                              :margin-left 1
                              :margin-right 1
                              :background (priority-color-scale day)}
                       (or (= day (- days-left))
                           (and (= day 5) (<= days-left -5))
                           (and (= day -5) (<= 5 days-left)))
                       (assoc :opacity 1))}]))]
        [:> hover-card/Portal
         [:> hover-card/Content {:class-name "data-[side=bottom]:animate-slideUpAndFade data-[side=right]:animate-slideLeftAndFade data-[side=left]:animate-slideRightAndFade data-[side=top]:animate-slideDownAndFade text-center rounded-md bg-white p-3 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] data-[state=open]:transition-all z-[60]"}
          [:div.text-xs
           [:div (tr "Customer delivery date")]
           [:div.font-bold (time-format/->str {:format :date :style :short} date-time)]]]]]))))



(defn location-summary-map [point]
  (let [!map-ref (hooks/use-ref nil)]
    (mapbox/use-map !map-ref {:center point
                              :on-load (fn [^js the-map]
                                         (.. (js/mapboxgl.Marker.)
                                             (setLngLat (clj->js point))
                                             (addTo the-map)))})
    [:div.w-full.h-40.rounded-md.shadow.border.border-slate-300.mb-3 {:ref !map-ref}]))

(defn location-summary [{:location/keys [summary details] :keys [viewer/css-class]}]
  [:> hover-card/Root {:open-delay 150 :close-delay 0}
   [:> hover-card/Trigger
    [:div.underline.decoration-indigo-100.hover:decoration-indigo-200.underline-offset-4
     {:class css-class}
     summary]]
   [:> hover-card/Portal
    [:> hover-card/Content {:class-name "data-[side=bottom]:animate-slideUpAndFade data-[side=right]:animate-slideLeftAndFade data-[side=left]:animate-slideRightAndFade data-[side=top]:animate-slideDownAndFade w-[300px] rounded-md bg-white p-3 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] data-[state=open]:transition-all z-[60]"}
     [:div.text-xs
      [location-summary-map [(:location/longitude details) (:location/latitude details)]]
      [:div
       [:span.font-bold (:address/name details)] [:br]
       (str (:address/postcode details) " " (:address/city details)) [:br]
       (-> details :address/country :country/code)]]]]])



(defn font [{:keys [font value viewer/css-class]}]
  [:div
   {:class
    [(case font
       :mono "font-mono"
       :tabular "tabular-nums")
     css-class]}
   value])
